import React, { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PiList, PiMagnifyingGlass, PiGear, PiGearFill } from 'react-icons/pi'
import { NotificationButton } from 'components/Notification/NotificationButton'
import { useAuth } from 'context/authentication/useAuth'
import { useLeftNavStore } from 'components/Layout/LeftNav'
import { useLayoutConfig } from 'context/layout'
import { Z_INDEX } from 'styles/z-index'
import { Breadcrumbs } from './Breadcrumbs'
import {
  getActiveNavSection,
  getNavItem,
  resolveChildren,
  isNavItemEnabled,
  resolvePath,
} from 'components/Layout/LeftNav/useNavItems'
import type { NavItemConfig } from 'components/Layout/LeftNav/types'
import { useNavItems } from 'components/Layout/LeftNav/NavItemsContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import type { BreadcrumbItem } from './types'
import type { NavChildItem } from 'components/Layout/LeftNav/types'
import type { FeatureFlags } from 'utils/useFeatureFlagService'
import type { User } from 'models/user'
import logoWhite from 'assets/Name - White Transparent.svg'

// ---------------------------------------------------------------------------
// Generic breadcrumb builder
// ---------------------------------------------------------------------------

/**
 * Find the active first-level child by checking sub-children paths first
 * (for nested items), then falling back to the child's own path.
 */
function findActiveChild(
  children: NavChildItem[],
  pathname: string
): NavChildItem | undefined {
  return children.find((child) => {
    if (child.children?.length) {
      return child.children.some((sub) =>
        pathname.startsWith(resolvePath(sub.path))
      )
    }
    return pathname.startsWith(resolvePath(child.path))
  })
}

/**
 * Build breadcrumb dropdowns from nav children.
 *
 * - Always creates a first-level dropdown for the children.
 * - If the active child has `showChildrenBreadcrumb: true` and nested children,
 *   a second-level dropdown is added automatically.
 */
function buildBreadcrumbs(
  children: NavChildItem[],
  pathname: string,
  featureFlags: FeatureFlags,
  user?: User
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []

  const filtered = children.filter((child) =>
    isNavItemEnabled(child, featureFlags, user)
  )

  const activeChild = findActiveChild(filtered, pathname)

  // First dropdown: group/category level
  breadcrumbs.push({
    label: activeChild?.label || filtered[0]?.label || '',
    hasDropdown: true,
    dropdownItems: filtered.map((child) => {
      const firstVisibleSub = child.children?.filter((sub) =>
        isNavItemEnabled(sub, featureFlags, user)
      )?.[0]

      return {
        label: child.label,
        description: child.description,
        path: firstVisibleSub
          ? resolvePath(firstVisibleSub.path)
          : resolvePath(child.path),
        isActive: child === activeChild,
      }
    }),
  })

  // Second dropdown: nested children (opt-in via showChildrenBreadcrumb)
  if (activeChild?.showChildrenBreadcrumb && activeChild.children?.length) {
    const filteredSubs = activeChild.children.filter((sub) =>
      isNavItemEnabled(sub, featureFlags, user)
    )
    const activeSub = filteredSubs.find((sub) =>
      pathname.startsWith(resolvePath(sub.path))
    )

    breadcrumbs.push({
      label: activeSub?.label || filteredSubs[0]?.label || '',
      hasDropdown: true,
      dropdownItems: filteredSubs.map((sub) => ({
        label: sub.label,
        path: resolvePath(sub.path),
        isActive: sub === activeSub,
      })),
    })
  }

  return breadcrumbs
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface HeaderProps {
  onSearchClick?: () => void
}

export const Header: React.FC<HeaderProps> = ({
  onSearchClick,
}): React.ReactElement => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAllowed } = useAuth()
  const {
    isCollapsed,
    toggleCollapsed,
    activeSection,
    isSlideOutOpen,
    openSlideOut,
    closeSlideOut,
    lastVisited,
  } = useLeftNavStore()
  const { config } = useLayoutConfig()
  const featureFlags = useFeatureFlagService()
  const { navItems } = useNavItems()

  // Build breadcrumbs from nav items
  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const path = location.pathname
    const breadcrumbs: BreadcrumbItem[] = []

    // Entity pages set categoryInfo via layout config — use it to build a
    // synthetic path that resolves to the correct nav section generically.
    const effectivePath = config.categoryInfo
      ? `/targets/${config.categoryInfo.categorySlug}/${config.categoryInfo.subCategorySlug}`
      : path

    const activeSection = getActiveNavSection(effectivePath, navItems)
    if (!activeSection) return breadcrumbs

    const navItem = getNavItem(navItems, activeSection)
    if (!navItem) return breadcrumbs

    // Section label — deals & campaigns show "All X" with a link back to the list view
    const isDealOrCampaign =
      navItem.id === 'deals' || navItem.id === 'campaigns'
    breadcrumbs.push({
      label: isDealOrCampaign
        ? `All ${navItem.label}`.toUpperCase()
        : navItem.label.toUpperCase(),
      isSectionLabel: true,
      ...(isDealOrCampaign && { path: resolvePath(navItem.path) }),
    })

    const children = resolveChildren(navItem)
    if (children?.length) {
      if (children.length > 1) {
        breadcrumbs.push(
          ...buildBreadcrumbs(children, effectivePath, featureFlags, user)
        )
      } else {
        breadcrumbs.push({
          label: children[0].label,
          path: resolvePath(children[0].path),
        })
      }
    }

    // Entity pages append chain/door name breadcrumbs
    if (config.entityBreadcrumbs?.length) {
      breadcrumbs.push(...config.entityBreadcrumbs)
    }

    return breadcrumbs
  }, [
    location.pathname,
    navItems,
    featureFlags,
    user,
    config.categoryInfo,
    config.entityBreadcrumbs,
  ])

  const handleMenuToggle = useCallback(() => {
    toggleCollapsed()
  }, [toggleCollapsed])

  const handleSearchClick = useCallback(() => {
    if (onSearchClick) {
      onSearchClick()
    }
  }, [onSearchClick])

  // Two distinct states for the settings button:
  // - isSettingsSlideOutOpen drives the click-toggle (open → close, closed → open)
  // - isSettingsActive drives the visual highlight; it also stays on while the
  //   user is anywhere under /settings/*, even after they've closed the panel.
  const isSettingsSlideOutOpen = activeSection === 'settings' && isSlideOutOpen
  const isSettingsActive =
    isSettingsSlideOutOpen || location.pathname.startsWith('/settings')

  const handleSettingsClick = useCallback(() => {
    if (isSettingsSlideOutOpen) {
      closeSlideOut()
      return
    }
    openSlideOut('settings')
    navigate(lastVisited.settings ?? '/settings/team/users')
  }, [
    isSettingsSlideOutOpen,
    openSlideOut,
    closeSlideOut,
    navigate,
    lastVisited.settings,
  ])

  // Mirror LeftNavBar's gating so future feature flags or role tweaks on the
  // settings nav item are respected here too.
  const settingsNavItem = useMemo<NavItemConfig | undefined>(
    () => getNavItem(navItems, 'settings'),
    [navItems]
  )
  const canAccessSettings =
    !!settingsNavItem &&
    isNavItemEnabled(settingsNavItem, featureFlags, user) &&
    isAllowed(settingsNavItem.minimumRole ?? 'Standard')

  return (
    <header
      className="flex items-center h-[var(--header-height)] min-h-[var(--header-height)] bg-[var(--nav-bg-primary)] pr-6 relative"
      style={{ zIndex: Z_INDEX.NAV }}
    >
      {/* Menu Toggle Button */}
      <button
        onClick={handleMenuToggle}
        aria-label="Toggle navigation menu"
        aria-expanded={!isCollapsed}
        className="flex items-center justify-center w-[var(--left-nav-width)] h-full border-none bg-transparent text-[var(--nav-text-muted)] cursor-pointer transition-all duration-150 hover:text-[var(--nav-text-primary)] hover:bg-[var(--nav-bg-hover)]"
      >
        <PiList size={24} />
      </button>

      {/* Logo */}
      <div className="flex items-center justify-center py-[18px]">
        <img src={logoWhite} alt="First Bite" className="h-[45px]" />
      </div>

      {/* Breadcrumbs */}
      <div className="flex-1 flex items-center px-6 min-w-0">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* Search Bar */}
        <button
          onClick={handleSearchClick}
          aria-label="Search First Bite"
          className="flex items-center gap-2 w-[220px] h-8 px-2 py-1 bg-sage-extra-lt border-none rounded-lg text-fg-secondary text-xs cursor-pointer transition-shadow duration-150 shadow-[0px_6px_58px_0px_rgba(196,203,214,0.1)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] max-[1280px]:w-8 max-[1280px]:px-1 max-[1280px]:justify-center"
        >
          <PiMagnifyingGlass
            size={16}
            className="text-fg-secondary flex-shrink-0"
          />
          <span className="flex-1 text-left max-[1280px]:hidden">
            Search First Bite
          </span>
        </button>

        {/* Settings Button */}
        {canAccessSettings && (
          <button
            type="button"
            onClick={handleSettingsClick}
            aria-label="Settings"
            aria-pressed={isSettingsSlideOutOpen}
            className={`flex items-center justify-center w-8 h-8 p-0 border-none rounded-lg text-[var(--nav-text-primary)] cursor-pointer transition-colors duration-150 hover:bg-[var(--nav-bg-hover)] ${
              isSettingsActive ? 'bg-[var(--nav-bg-hover)]' : 'bg-transparent'
            }`}
          >
            {isSettingsActive ? (
              <PiGearFill size={24} className="text-white" />
            ) : (
              <PiGear size={24} className="text-white" />
            )}
          </button>
        )}

        {/* Notification Button */}
        <NotificationButton />
      </div>
    </header>
  )
}
